import { useEffect, useMemo, useRef, useState } from "react";

import {
  useAssignAdminV2StaffRoleMutation,
  useGetAdminV2MeQuery,
  useGetAdminV2RbacPermissionsQuery,
  useGetAdminV2RbacRolesQuery,
  useGetAdminV2RbacStaffUsersQuery,
  useSetAdminV2StaffPermissionOverrideMutation,
} from "@/store/api/baseApi";
import type { AdminV2RbacStaffUsersResponse } from "@/types/adminV2";
import { getErrorMessage } from "@/utils/errorHandling";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import { useDebouncedValue } from "@/vnext/shared/useDebouncedValue";

export type OverrideMode = "grant" | "deny";
export type OverrideModeSelection = OverrideMode | "";
export type TeamPermissionsStaffUser =
  AdminV2RbacStaffUsersResponse["staffUsers"][number];

export interface TeamPermissionsRoleReviewRequest {
  staffUser: TeamPermissionsStaffUser;
  roleKey: string;
  isAssigned: boolean;
}

export interface TeamPermissionsOverrideReviewRequest {
  staffUser: TeamPermissionsStaffUser;
  permissionKey: string;
  overrideMode: OverrideMode;
  reason: string;
}

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "TeamPermissionsPage",
);
const SELF_LOCKOUT_GUARD_ROLE_KEY = "super_administrator";
const SELF_LOCKOUT_GUARD_MESSAGE =
  "You cannot remove your own Super Administrator role.";

const getOverrideUpsertBlockReason = ({
  hasSelectedStaffUser,
  overrideMode,
  permissionKey,
  reason,
}: {
  hasSelectedStaffUser: boolean;
  overrideMode: OverrideModeSelection;
  permissionKey: string;
  reason: string;
}): string | null => {
  if (!hasSelectedStaffUser) {
    return "Select a staff user before applying an override.";
  }

  if (!permissionKey) {
    return "Choose a permission key before applying an override.";
  }

  if (!overrideMode) {
    return "Choose whether to grant or deny the selected permission before applying an override.";
  }

  if (reason.trim().length === 0) {
    return "Add a short reason before applying this one-off change.";
  }

  return null;
};

export const useTeamPermissionsPageState = () => {
  const [staffSearch, setStaffSearch] = useState("");
  const [selectedStaffUserId, setSelectedStaffUserId] = useState<string | null>(
    null,
  );
  const [overridePermissionKey, setOverridePermissionKey] = useState("");
  const [overrideMode, setOverrideMode] = useState<OverrideModeSelection>("");
  const [overrideReason, setOverrideReason] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const debouncedStaffSearch = useDebouncedValue(staffSearch);
  const normalizedStaffSearch = staffSearch.trim();
  const normalizedDebouncedStaffSearch = debouncedStaffSearch.trim();
  const hasPendingStaffSearch =
    normalizedStaffSearch !== normalizedDebouncedStaffSearch;
  const [assignStaffRole, assignStaffRoleState] =
    useAssignAdminV2StaffRoleMutation();
  const [setPermissionOverride, setPermissionOverrideState] =
    useSetAdminV2StaffPermissionOverrideMutation();

  const { data: meData } = useGetAdminV2MeQuery();
  const {
    data: staffUsersData,
    isLoading: staffUsersLoading,
    isError: staffUsersError,
    refetch: refetchStaffUsers,
  } = useGetAdminV2RbacStaffUsersQuery({
    limit: 200,
    ...(normalizedDebouncedStaffSearch.length > 0
      ? { search: normalizedDebouncedStaffSearch }
      : {}),
  });
  const {
    data: rolesData,
    isLoading: rolesLoading,
    isError: rolesError,
  } = useGetAdminV2RbacRolesQuery();
  const {
    data: permissionsData,
    isLoading: permissionsLoading,
    isError: permissionsError,
  } = useGetAdminV2RbacPermissionsQuery();

  const staffUsers = useMemo(
    () => staffUsersData?.staffUsers ?? [],
    [staffUsersData?.staffUsers],
  );
  const roles = rolesData?.roles ?? [];
  const permissions = permissionsData?.permissions ?? [];
  const pageStartedAtRef = useRef<number | null>(null);
  const readyLoggedRef = useRef(false);
  const pageFailureLoggedRef = useRef(false);
  const effectiveSelectedStaffUserId = useMemo(() => {
    if (staffUsers.length === 0) {
      return null;
    }

    if (
      selectedStaffUserId !== null &&
      staffUsers.some((user) => user.id === selectedStaffUserId)
    ) {
      return selectedStaffUserId;
    }

    // Keep a usable default selection visible as soon as staff data arrives.
    return staffUsers[0]?.id ?? null;
  }, [staffUsers, selectedStaffUserId]);

  useEffect(() => {
    pageStartedAtRef.current = Date.now();
    logger.debug("Team permissions page entered", {
      feature: "FND",
      operation: "TEAM_PERMISSIONS_PAGE_ENTER",
      component: "TeamPermissionsPage",
    });
    readyLoggedRef.current = false;
    pageFailureLoggedRef.current = false;
  }, []);

  useEffect(() => {
    if (staffUsersLoading || rolesLoading || permissionsLoading) {
      return;
    }

    if (staffUsersError || rolesError || permissionsError) {
      if (!pageFailureLoggedRef.current) {
        pageFailureLoggedRef.current = true;
        logger.error(
          "Team permissions page failed to load",
          new Error("team_permissions_page_load_failed"),
          {
            feature: "FND",
            operation: "TEAM_PERMISSIONS_PAGE_FAIL",
            component: "TeamPermissionsPage",
            staffUserCount: staffUsers.length,
            roleCount: roles.length,
            permissionCount: permissions.length,
            source: staffUsersError
              ? "staff-users"
              : rolesError
                ? "roles"
                : "permissions",
          },
        );
      }
      return;
    }

    if (readyLoggedRef.current) {
      return;
    }

    readyLoggedRef.current = true;
    logger.debug("Team permissions page ready", {
      feature: "FND",
      operation: "TEAM_PERMISSIONS_PAGE_READY",
      component: "TeamPermissionsPage",
      staffUserCount: staffUsers.length,
      roleCount: roles.length,
      permissionCount: permissions.length,
      selectedStaffUserId: effectiveSelectedStaffUserId,
      pageLoadDurationMs:
        pageStartedAtRef.current === null
          ? null
          : Date.now() - pageStartedAtRef.current,
    });
  }, [
    staffUsersLoading,
    rolesLoading,
    permissionsLoading,
    rolesError,
    permissionsError,
    staffUsersError,
    roles.length,
    permissions.length,
    staffUsers.length,
    effectiveSelectedStaffUserId,
  ]);

  const selectedStaffUser = useMemo(
    () =>
      effectiveSelectedStaffUserId
        ? (staffUsers.find(
            (user) => user.id === effectiveSelectedStaffUserId,
          ) ?? null)
        : null,
    [effectiveSelectedStaffUserId, staffUsers],
  );
  const overrideUpsertBlockReason = useMemo(
    () =>
      getOverrideUpsertBlockReason({
        hasSelectedStaffUser: Boolean(selectedStaffUser),
        permissionKey: overridePermissionKey,
        overrideMode,
        reason: overrideReason,
      }),
    [overrideMode, overridePermissionKey, overrideReason, selectedStaffUser],
  );

  const assignedRoleKeys = useMemo(
    () => new Set((selectedStaffUser?.roles ?? []).map((role) => role.roleKey)),
    [selectedStaffUser],
  );
  const overrideApplyStateRef = useRef<string | null>(null);

  useEffect(() => {
    const nextStateKey = overrideUpsertBlockReason ?? "__ready__";
    if (overrideApplyStateRef.current === nextStateKey) {
      return;
    }

    overrideApplyStateRef.current = nextStateKey;
    logger.debug("Team permissions override apply state changed", {
      feature: "FND",
      operation: "TEAM_PERMISSIONS_OVERRIDE_APPLY_STATE",
      component: "TeamPermissionsPage",
      state: overrideUpsertBlockReason ? "blocked" : "ready",
      reason: overrideUpsertBlockReason ?? undefined,
      source: "derived_form_state",
      staffUserId: effectiveSelectedStaffUserId ?? undefined,
      permissionKey: overridePermissionKey || undefined,
      overrideMode: overrideMode || undefined,
    });
  }, [
    effectiveSelectedStaffUserId,
    overrideMode,
    overridePermissionKey,
    overrideUpsertBlockReason,
  ]);

  const setSearchValue = (nextSearch: string): void => {
    logger.debug("Team permissions search changed", {
      feature: "FND",
      operation: "TEAM_PERMISSIONS_SEARCH_CHANGED",
      component: "TeamPermissionsPage",
      previousSearchLength: normalizedStaffSearch.length,
      nextSearchLength: nextSearch.trim().length,
      roleCount: roles.length,
      permissionCount: permissions.length,
    });
    setStaffSearch(nextSearch);
  };

  const trackStaffSelection = (nextStaffUserId: string): void => {
    if (nextStaffUserId === selectedStaffUserId) {
      return;
    }

    logger.debug("Team permissions staff selected", {
      feature: "FND",
      operation: "TEAM_PERMISSIONS_STAFF_SELECTED",
      component: "TeamPermissionsPage",
      fromStaffUserId: selectedStaffUserId,
      toStaffUserId: nextStaffUserId,
      source: "user",
    });
    setSelectedStaffUserId(nextStaffUserId);
  };

  const setOverridePermission = (nextPermissionKey: string): void => {
    logger.debug("Team permissions override permission changed", {
      feature: "FND",
      operation: "TEAM_PERMISSIONS_OVERRIDE_PERMISSION_CHANGED",
      component: "TeamPermissionsPage",
      previousPermissionKey: overridePermissionKey,
      nextPermissionKey,
      source: "user",
      nextApplyState: getOverrideUpsertBlockReason({
        hasSelectedStaffUser: Boolean(selectedStaffUser),
        permissionKey: nextPermissionKey,
        overrideMode,
        reason: overrideReason,
      })
        ? "blocked"
        : "ready",
    });
    setOverridePermissionKey(nextPermissionKey);
  };

  const setOverrideModeValue = (nextMode: OverrideModeSelection): void => {
    logger.debug("Team permissions override mode changed", {
      feature: "FND",
      operation: "TEAM_PERMISSIONS_OVERRIDE_MODE_CHANGED",
      component: "TeamPermissionsPage",
      previousMode: overrideMode,
      nextMode,
      permissionKey: overridePermissionKey,
      source: "user",
      nextApplyState: getOverrideUpsertBlockReason({
        hasSelectedStaffUser: Boolean(selectedStaffUser),
        permissionKey: overridePermissionKey,
        overrideMode: nextMode,
        reason: overrideReason,
      })
        ? "blocked"
        : "ready",
    });
    setOverrideMode(nextMode);
  };

  const setOverrideReasonValue = (nextReason: string): void => {
    logger.debug("Team permissions override reason changed", {
      feature: "FND",
      operation: "TEAM_PERMISSIONS_OVERRIDE_REASON_CHANGED",
      component: "TeamPermissionsPage",
      reasonLength: nextReason.trim().length,
      permissionKey: overridePermissionKey,
      staffUserId: effectiveSelectedStaffUserId ?? undefined,
    });
    setOverrideReason(nextReason);
  };

  const refreshStaffUsers = (): void => {
    logger.debug("Team permissions refresh requested", {
      feature: "FND",
      operation: "TEAM_PERMISSIONS_REFRESH_REQUESTED",
      component: "TeamPermissionsPage",
      staffSearchLength: normalizedStaffSearch.length,
      staffUserCount: staffUsers.length,
    });
    void refetchStaffUsers();
  };

  const getRoleToggleBlockReasonForStaff = (
    staffUser: TeamPermissionsStaffUser | null,
    roleKey: string,
    isAssigned: boolean,
  ): string | null => {
    if (
      isAssigned &&
      roleKey === SELF_LOCKOUT_GUARD_ROLE_KEY &&
      Boolean(staffUser && staffUser.id === meData?.staffUser.id)
    ) {
      return SELF_LOCKOUT_GUARD_MESSAGE;
    }

    return null;
  };

  const getRoleToggleBlockReason = (
    roleKey: string,
    isAssigned: boolean,
  ): string | null =>
    getRoleToggleBlockReasonForStaff(selectedStaffUser, roleKey, isAssigned);

  const runRoleToggle = async (
    roleKey: string,
    isAssigned: boolean,
    reviewRequest?: TeamPermissionsRoleReviewRequest,
  ) => {
    const activeStaffUser = reviewRequest?.staffUser ?? selectedStaffUser;

    if (!activeStaffUser) {
      logger.warn("Team permissions role toggle blocked", {
        feature: "FND",
        operation: "TEAM_PERMISSIONS_ROLE_TOGGLE_BLOCKED",
        component: "TeamPermissionsPage",
        roleKey,
        action: isAssigned ? "remove" : "assign",
      });
      return;
    }
    const roleToggleBlockReason = getRoleToggleBlockReasonForStaff(
      activeStaffUser,
      roleKey,
      isAssigned,
    );
    if (roleToggleBlockReason) {
      setActionError(roleToggleBlockReason);
      logger.warn("Team permissions role toggle blocked", {
        feature: "FND",
        operation: "TEAM_PERMISSIONS_ROLE_TOGGLE_BLOCKED",
        component: "TeamPermissionsPage",
        staffUserId: activeStaffUser.id,
        roleKey,
        action: "remove",
        reason: "self_lockout_protection",
      });
      return;
    }

    setActionError(null);
    const startedAt = Date.now();
    logger.debug("Team permissions role toggle started", {
      feature: "FND",
      operation: "TEAM_PERMISSIONS_ROLE_TOGGLE_START",
      component: "TeamPermissionsPage",
      staffUserId: activeStaffUser.id,
      roleKey,
      action: isAssigned ? "remove" : "assign",
    });
    try {
      await assignStaffRole({
        staffUserId: activeStaffUser.id,
        roleKey,
        assign: !isAssigned,
      }).unwrap();
      await refetchStaffUsers();
      logger.debug("Team permissions role toggle succeeded", {
        feature: "FND",
        operation: "TEAM_PERMISSIONS_ROLE_TOGGLE_SUCCESS",
        component: "TeamPermissionsPage",
        staffUserId: activeStaffUser.id,
        roleKey,
        action: isAssigned ? "remove" : "assign",
        durationMs: Date.now() - startedAt,
      });
    } catch (error) {
      const mappedMessage = getErrorMessage(
        error,
        "Failed to update role assignment.",
      );
      const message =
        error instanceof Error && mappedMessage === error.message
          ? "Failed to update role assignment."
          : mappedMessage;
      setActionError(message);
      logger.error(
        "Team permissions role toggle failed",
        error instanceof Error ? error : undefined,
        {
          feature: "FND",
          operation: "TEAM_PERMISSIONS_ROLE_TOGGLE_FAIL",
          component: "TeamPermissionsPage",
          staffUserId: activeStaffUser.id,
          roleKey,
          action: isAssigned ? "remove" : "assign",
          durationMs: Date.now() - startedAt,
        },
      );
    }
  };

  const runOverrideUpsert = async (
    reviewRequest?: TeamPermissionsOverrideReviewRequest,
  ) => {
    const activeStaffUser = reviewRequest?.staffUser ?? selectedStaffUser;
    const activePermissionKey =
      reviewRequest?.permissionKey ?? overridePermissionKey;
    const activeOverrideMode = reviewRequest?.overrideMode ?? overrideMode;
    const activeReason = reviewRequest?.reason ?? overrideReason;
    const activeBlockReason = getOverrideUpsertBlockReason({
      hasSelectedStaffUser: Boolean(activeStaffUser),
      permissionKey: activePermissionKey,
      overrideMode: activeOverrideMode,
      reason: activeReason,
    });

    if (activeBlockReason) {
      logger.warn("Team permissions override blocked", {
        feature: "FND",
        operation: "TEAM_PERMISSIONS_OVERRIDE_UPSERT_BLOCKED",
        component: "TeamPermissionsPage",
        reason: activeBlockReason,
      });
      return;
    }

    if (!activeStaffUser || activeOverrideMode === "") {
      return;
    }

    setActionError(null);
    const startedAt = Date.now();
    logger.debug("Team permissions override started", {
      feature: "FND",
      operation: "TEAM_PERMISSIONS_OVERRIDE_UPSERT_START",
      component: "TeamPermissionsPage",
      staffUserId: activeStaffUser.id,
      permissionKey: activePermissionKey,
      isGranted: activeOverrideMode === "grant",
      hasReason: activeReason.trim().length > 0,
    });
    try {
      await setPermissionOverride({
        staffUserId: activeStaffUser.id,
        permissionKey: activePermissionKey,
        isGranted: activeOverrideMode === "grant",
        ...(activeReason.trim().length > 0
          ? { reason: activeReason.trim() }
          : {}),
      }).unwrap();
      setOverrideReason("");
      await refetchStaffUsers();
      logger.debug("Team permissions override succeeded", {
        feature: "FND",
        operation: "TEAM_PERMISSIONS_OVERRIDE_UPSERT_SUCCESS",
        component: "TeamPermissionsPage",
        staffUserId: activeStaffUser.id,
        permissionKey: activePermissionKey,
        isGranted: activeOverrideMode === "grant",
        hasReason: activeReason.trim().length > 0,
        durationMs: Date.now() - startedAt,
      });
    } catch (error) {
      const mappedMessage = getErrorMessage(
        error,
        "Failed to update permission override.",
      );
      const message =
        error instanceof Error && mappedMessage === error.message
          ? "Failed to update permission override."
          : mappedMessage;
      setActionError(message);
      logger.error(
        "Team permissions override failed",
        error instanceof Error ? error : undefined,
        {
          feature: "FND",
          operation: "TEAM_PERMISSIONS_OVERRIDE_UPSERT_FAIL",
          component: "TeamPermissionsPage",
          staffUserId: activeStaffUser.id,
          permissionKey: activePermissionKey,
          isGranted: activeOverrideMode === "grant",
          durationMs: Date.now() - startedAt,
        },
      );
    }
  };

  return {
    meData,
    staffUsersData,
    rolesData,
    permissionsData,
    staffUsersLoading,
    rolesLoading,
    permissionsLoading,
    staffUsersError,
    rolesError,
    permissionsError,
    staffSearch,
    selectedStaffUserId: effectiveSelectedStaffUserId,
    overridePermissionKey,
    overrideMode,
    overrideReason,
    actionError,
    hasPendingStaffSearch,
    staffUsers,
    roles,
    permissions,
    selectedStaffUser,
    assignedRoleKeys,
    overrideUpsertBlockReason,
    isAssignStaffRoleLoading: assignStaffRoleState.isLoading,
    isSetPermissionOverrideLoading: setPermissionOverrideState.isLoading,
    setSearchValue,
    trackStaffSelection,
    setOverridePermission,
    setOverrideModeValue,
    setOverrideReasonValue,
    refreshStaffUsers,
    runRoleToggle,
    getRoleToggleBlockReason,
    runOverrideUpsert,
  };
};
